import { useState } from "react";
import { motion } from "framer-motion";
import {
  CheckCircle2, Flag, Video, FileImage, ExternalLink, ShieldAlert, ShieldCheck, MessageSquareWarning, Mail,
  Landmark, Images, ChevronDown, ChevronUp, Eye, EyeOff, PenLine, Sparkles, Phone, MapPin, GraduationCap, CalendarDays,
} from "lucide-react";
import { Link } from "react-router-dom";
import { SelectCheckbox } from "./SelectCheckbox";
import { EditDiff } from "./EditDiff";
import { formatDollars } from "../../lib/draftCampaign";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "mra_jwt";

const authedFileSrc = (fileId) => {
  const t = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  return `${BACKEND_ORIGIN}/api/files/${fileId}${t ? `?auth=${t}` : ""}`;
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }); } catch { return iso; }
};

const PAYOUT = {
  not_started: { label: "Payouts not started", cls: "bg-sand text-olive ring-1 ring-inset ring-ink/15" },
  in_progress: { label: "Payouts in progress", cls: "bg-yellow/30 text-ink" },
  needs_info: { label: "Payouts need info", cls: "bg-tangerine/15 text-tangerine" },
  in_review: { label: "Payouts verifying", cls: "bg-cobalt/12 text-cobalt" },
  ready: { label: "Payouts ready", cls: "bg-grass/12 text-grass" },
};

const Pill = ({ icon: Icon, cls, children, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${cls}`}>
    {Icon && <Icon className="h-3 w-3" strokeWidth={2.5} />} {children}
  </span>
);

const Detail = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-olive">{label}</p>
    <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-ink">{value || <em className="italic text-olive">—</em>}</p>
  </div>
);

const Package = ({ c }) => {
  const att = c.attestations || {};
  const attCount = ["eligibility", "enrollment_consent", "student_cert"].filter((k) => att[k]).length;
  return (
    <div className="mt-4 grid grid-cols-1 gap-5 rounded-lg border border-border bg-sand p-5 md:grid-cols-2" data-testid={`review-package-${c.id}`}>
      <Detail label="Headline" value={c.headline} />
      <Detail label="Program · class" value={[c.program, c.grad_year ? `Class of '${String(c.grad_year).slice(-2)}` : null].filter(Boolean).join(" · ")} />
      <div className="md:col-span-2"><Detail label="Bio" value={c.bio} testid={`review-bio-${c.id}`} /></div>
      <div className="md:col-span-2"><Detail label="Funding need" value={c.story} testid={`review-story-${c.id}`} /></div>
      <Detail label="Deadline" value={c.deadline} />
      <Detail label="Visibility" value={c.visibility === "private" ? "Private — link only" : "Public — discoverable"} />
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-olive">Private to reviewers</p>
        <p className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ink">
          <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5 text-olive" /> {c.phone || "—"}</span>
          <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-olive" /> {c.location || "—"}</span>
          <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5 text-olive" /> {c.student_email}</span>
        </p>
      </div>
      <div data-testid={`review-attestations-${c.id}`}>
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-olive">Attestations</p>
        <p className="mt-1 text-sm text-ink">
          {attCount === 3 ? <>All three signed{c.attested_at ? ` · ${fmtDate(c.attested_at)}` : ""}</> : <span className="text-scarlet">{attCount} of 3 signed</span>}
        </p>
        <p className="mt-0.5 text-xs text-olive">Eligibility · Enrollment consent · Student certification</p>
      </div>
      {c.photo_file_ids?.length > 0 && (
        <div className="md:col-span-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-olive">Photos</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {c.photo_file_ids.map((id) => <img key={id} src={authedFileSrc(id)} alt="" className="h-16 w-16 rounded-md object-cover" />)}
          </div>
        </div>
      )}
      {c.intro_video_poster_url && (
        <div className="md:col-span-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-olive">Video pitch · {c.intro_video_status}</p>
          <img src={c.intro_video_poster_url} alt="" className="mt-2 h-28 rounded-md object-cover" />
        </div>
      )}
    </div>
  );
};

export const ReviewRow = ({ c, checked, onToggle, onSingleApprove, onSingleReject, onPreviewEmail }) => {
  const [open, setOpen] = useState(false);
  const isEdit = c.kind === "edit";
  const payout = PAYOUT[c.payout_state] || PAYOUT.not_started;
  const attCount = ["eligibility", "enrollment_consent", "student_cert"].filter((k) => c.attestations?.[k]).length;
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6, height: 0 }}
      transition={{ duration: 0.25, ease: [0.22, 0.61, 0.36, 1] }}
      className={`flex items-start gap-4 rounded-xl border-2 p-5 transition-colors ${checked ? "border-ink bg-yellow/5" : "border-ink/10 bg-bone hover:border-ink/25"}`}
      data-testid={`review-row-${c.id}`}
      data-kind={c.kind}
    >
      <div className="pt-1">
        <SelectCheckbox checked={checked} onChange={onToggle} testid={`review-checkbox-${c.id}`} label={`Select ${c.title || c.id}`} />
      </div>

      {c.cover_file_id ? (
        <img src={authedFileSrc(c.cover_file_id)} alt="" className="h-20 w-20 shrink-0 rounded-md object-cover" />
      ) : (
        <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-md bg-cobalt font-serif text-3xl italic text-sand" data-testid={`review-name-tile-${c.id}`}>
          {(c.student_name || c.student_email || "?").trim().charAt(0).toUpperCase()}
        </div>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className="flex min-w-0 items-baseline gap-2">
            <Pill icon={isEdit ? PenLine : Sparkles} cls={isEdit ? "bg-cobalt/12 text-cobalt" : "bg-yellow/30 text-ink"} testid={`review-kind-${c.id}`}>
              {isEdit ? "Edit to live page" : "New campaign"}
            </Pill>
            <h3 className="truncate font-serif text-xl text-ink" data-testid={`review-title-${c.id}`}>
              {c.title || <em className="italic text-olive">Untitled campaign</em>}
            </h3>
          </div>
          <span className="shrink-0 text-[10px] uppercase tracking-[0.22em] text-olive">Submitted {fmtDate(c.submitted_at || c.updated_at)}</span>
        </div>
        <p className="mt-1 text-sm text-olive">
          <span className="font-semibold text-ink">{c.student_name || c.student_email}</span>
          {c.student_school && <> · {c.student_school}</>}
          {c.category && <> · {c.category}</>}
          {" · "}
          <span className="font-semibold text-ink">{formatDollars(c.goal_cents)}</span>
        </p>
        <p className="mt-2 line-clamp-2 text-sm text-ink">{c.story || <em className="italic text-olive">No funding need written yet.</em>}</p>

        {isEdit && <EditDiff c={c} />}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {c.student_enrollment_verified ? (
            <Pill icon={CheckCircle2} cls="bg-grass/12 text-grass" testid={`review-verified-pill-${c.id}`}>Enrollment verified</Pill>
          ) : (
            <Pill icon={ShieldAlert} cls="bg-tangerine/15 text-tangerine" testid={`review-unverified-pill-${c.id}`}>Enrollment · verifies on approval</Pill>
          )}
          {c.student_edu_verified && <Pill icon={ShieldCheck} cls="bg-grass/12 text-grass" testid={`review-edu-pill-${c.id}`}>.edu confirmed</Pill>}
          <Pill icon={Landmark} cls={payout.cls} testid={`review-payout-pill-${c.id}`}>{payout.label}</Pill>
          <Pill icon={CheckCircle2} cls={attCount === 3 ? "bg-grass/12 text-grass" : "bg-scarlet/12 text-scarlet"} testid={`review-attest-pill-${c.id}`}>
            {attCount}/3 attested
          </Pill>
          {c.intro_video_playback_id && <Pill icon={Video} cls="bg-cobalt/12 text-cobalt">Video</Pill>}
          {c.cover_file_id && <Pill icon={FileImage} cls="bg-tangerine/12 text-tangerine">Photo</Pill>}
          {c.photo_file_ids?.length > 0 && <Pill icon={Images} cls="bg-tangerine/12 text-tangerine">{c.photo_file_ids.length} photos</Pill>}
          {c.request_doc_file_id && (
            <a href={authedFileSrc(c.request_doc_file_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border border-border bg-sand px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-olive hover:border-ink">
              Doc <ExternalLink className="h-3 w-3" />
            </a>
          )}
          <Pill icon={c.visibility === "private" ? EyeOff : Eye} cls="bg-sand text-olive ring-1 ring-inset ring-ink/10">{c.visibility === "private" ? "Private" : "Public"}</Pill>
          {c.deadline && <Pill icon={CalendarDays} cls="bg-sand text-olive ring-1 ring-inset ring-ink/10">By {c.deadline}</Pill>}
          {c.grad_year && <Pill icon={GraduationCap} cls="bg-sand text-olive ring-1 ring-inset ring-ink/10">&rsquo;{String(c.grad_year).slice(-2)}</Pill>}
          {c.flag_type && <Pill icon={Flag} cls="bg-yellow/25 text-ink" testid={`review-flag-pill-${c.id}`}>{c.flag_type}</Pill>}
        </div>

        <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`review-toggle-package-${c.id}`} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.18em] text-olive hover:text-ink">
          {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />} {open ? "Hide full package" : "Full package"}
        </button>
        {open && <Package c={c} />}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button type="button" onClick={onSingleApprove} data-testid={`review-single-approve-${c.id}`} className="inline-flex items-center gap-1 rounded-full bg-ink px-4 py-1.5 text-xs font-semibold text-sand transition-colors hover:bg-forest">
            <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} /> {isEdit ? "Publish edits" : "Approve — verify & go live"}
          </button>
          <Link to={`/campaign/${c.id}`} target="_blank" rel="noopener noreferrer" data-testid={`review-open-page-${c.id}`} className="inline-flex items-center gap-1 rounded-full border border-border bg-bone px-4 py-1.5 text-xs font-semibold text-ink transition-colors hover:border-ink hover:bg-sand">
            <Eye className="h-3.5 w-3.5" strokeWidth={2} /> View page
          </Link>
          {!isEdit && (
            <button type="button" onClick={onPreviewEmail} data-testid={`review-preview-email-${c.id}`} className="inline-flex items-center gap-1 rounded-full border border-border bg-bone px-4 py-1.5 text-xs font-semibold text-ink transition-colors hover:border-ink hover:bg-sand">
              <Mail className="h-3.5 w-3.5" strokeWidth={2} /> Preview email
            </button>
          )}
          <button type="button" onClick={onSingleReject} data-testid={`review-single-reject-${c.id}`} className="inline-flex items-center gap-1 rounded-full border border-scarlet/40 bg-sand px-4 py-1.5 text-xs font-semibold text-scarlet transition-colors hover:bg-scarlet/8">
            <MessageSquareWarning className="h-3.5 w-3.5" strokeWidth={2} /> Return
          </button>
        </div>
      </div>
    </motion.article>
  );
};
